import {
  EMAIL_SESSION_CLAIM,
  BlockingFunctionRejectionCodes,
  NeoTaxEnvironment,
  ProvisioningErrorCodes,
  assertNever,
} from '@common/neo.tax';
import { captureException } from '@sentry/react';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import z from 'zod';

import { AuthErrorType, LocalStorageKey } from '@neo.tax/shared/enums';
import { ClientReportableLoginMethod } from '@neo.tax/shared/graphql/types';
import { env } from '@neo.tax/shared/utils';
import { toHttpUrl } from '@neo.tax/shared/utils/url.utils';

import { redirectToGoogleOAuth } from '../google';
import { redirectToMicrosoftOAuth } from '../microsoft';
import { AuthenticationUser, MaybeFirebaseError } from './firebase.types';
import { SsoProvider } from './sso-provider.enum';

declare global {
  interface Window {
    FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string;
  }
}

const INVALID_PASSWORD_ERROR = 'Invalid username and/or password.';
const SOMETHING_WENT_WRONG_ERROR = 'Something went wrong. Please try again.';
export const SERVICE_BUSY_ERROR =
  'Our system is taking longer than usual to respond. Please wait a moment and try again.';
// Deliberately offers no retry: the account cannot sign in until its identity provider is configured to
// release an email address, so the only way forward is someone changing that.
export const NO_PROVIDER_EMAIL_ERROR =
  'Your organization’s login provider did not share an email address with us, which is required to sign in. Please contact your IT administrator or Neo.Tax support.';

/** Ways a user can abandon the sign-in popup themselves, none of which are a fault worth recording. */
const POPUP_DISMISSAL_CODES = new Set(['auth/popup-closed-by-user', 'auth/cancelled-popup-request']);

/**
 * Recorded on the login audit row when Identity Platform refuses to attach a provider to an address that
 * already has an account. States the cause, not the remedy, which differs by provider.
 */
const ACCOUNT_EXISTS_REJECTION_REASON = 'Account exists with a different sign-in method';

/**
 * How each provider is named on a login-audit row.
 *
 * Written out rather than derived from the provider name, though several pairs match: the two vocabularies are
 * independent, and a `Record` keyed on the enum makes adding a provider fail to compile here rather than
 * record sign-ins under a method chosen by string coincidence.
 */
const SSO_PROVIDER_AUDIT_METHODS: Record<SsoProvider, ClientReportableLoginMethod> = {
  [SsoProvider.GOOGLE]: ClientReportableLoginMethod.GoogleSso,
  [SsoProvider.MICROSOFT]: ClientReportableLoginMethod.MicrosoftSso,
  [SsoProvider.OIDC]: ClientReportableLoginMethod.Oidc,
  [SsoProvider.SAML]: ClientReportableLoginMethod.Saml,
};

const FIREBASE_AUTH_EMULATOR_HOST = '127.0.0.1:9099';

const firebaseConfig = {
  apiKey: env.REACT_APP_FIREBASE_API_KEY,
  authDomain: `${env.REACT_APP_FIREBASE_PROJECT_ID}.firebaseapp.com`,
  projectId: env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: env.REACT_APP_FIREBASE_APP_ID,
};

const googleOAuthProvider = new firebase.auth.GoogleAuthProvider();
googleOAuthProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
googleOAuthProvider.addScope('https://www.googleapis.com/auth/userinfo.profile');

const microsoftOAuthProvider = new firebase.auth.OAuthProvider('microsoft.com');
// custom OAuth parameters: https://docs.microsoft.com/en-us/azure/active-directory/develop/v1-protocols-oauth-code
microsoftOAuthProvider.setCustomParameters({ prompt: 'consent' }); // force re-consent

const getAuthProvider = (
  ssoProvider: SsoProvider,
  enterpriseSsoProviderId?: string,
): firebase.auth.GoogleAuthProvider | firebase.auth.OAuthProvider | firebase.auth.SAMLAuthProvider => {
  switch (ssoProvider) {
    case SsoProvider.GOOGLE:
      return googleOAuthProvider;
    case SsoProvider.MICROSOFT:
      return microsoftOAuthProvider;
    case SsoProvider.OIDC:
      if (!enterpriseSsoProviderId) {
        throw new Error('Provider ID is required when using OIDC');
      }
      return new firebase.auth.OAuthProvider(enterpriseSsoProviderId);
    case SsoProvider.SAML:
      if (!enterpriseSsoProviderId) {
        throw new Error('Provider ID is required when using SAML');
      }
      return new firebase.auth.SAMLAuthProvider(enterpriseSsoProviderId);
    default:
      throw new Error(`Provider ${ssoProvider} is not supported`);
  }
};

export const initializeAuthenticationService = (): void => {
  const firebaseApp = firebase.initializeApp(firebaseConfig);

  if (env.REACT_APP_USE_FIREBASE_EMULATOR === true) {
    firebase.auth().useEmulator(`http://${FIREBASE_AUTH_EMULATOR_HOST}`);
    console.log('Firebase Auth emulator connected at ', FIREBASE_AUTH_EMULATOR_HOST);
    // Skip App Check entirely when using the Auth emulator. The emulator does
    // not enforce App Check, and initializeAppCheck would attempt a real
    // network call to the reCAPTCHA Enterprise backend.
    return;
  }

  if (env.REACT_APP_NEO_TAX_ENV === NeoTaxEnvironment.LOCAL) {
    // Real-Firebase local dev: print a debug token on first call to
    // initializeAppCheck. Engineers register the printed UUID in
    // Firebase Console → App Check → Apps → "Manage debug tokens".
    // Must be set before initializeAppCheck() is invoked.
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  try {
    initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaEnterpriseProvider(env.REACT_APP_FIREBASE_APP_CHECK_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    // Don't take auth down if App Check init fails (especially during the
    // unenforced rollout window). Surface to Sentry so we notice.
    captureException(error, { extra: { source: 'initializeAppCheck' } });
  }
};

export type EmailPasswordSignInFailure = {
  /**
   * Whether Firebase rejected the credentials themselves, as opposed to the attempt failing for a
   * reason the password had nothing to do with. Callers need the distinction because the login form
   * has richer copy for a bad credential — it points magic-link users at their link — which is actively
   * misleading advice when the password was never in question.
   */
  invalidCredentials: boolean;
  message: string;
};

export const signInWithEmailAndPassword = async (
  email: string,
  password: string,
): Promise<EmailPasswordSignInFailure | undefined> => {
  try {
    await firebase.auth().signInWithEmailAndPassword(email, password);
  } catch (error) {
    const firebaseError = error as firebase.FirebaseError;
    if (
      firebaseError.code &&
      (firebaseError.code === 'auth/wrong-password' || firebaseError.code === 'auth/invalid-credential')
    ) {
      return { invalidCredentials: true, message: INVALID_PASSWORD_ERROR };
    }
    captureException(firebaseError, { extra: { source: 'signInWithEmailAndPassword' } });
    // Email/password sign-in reaches the sign-in blocking function too, so it inherits that function's
    // failure modes even though it needs nothing from it. "Something went wrong" reads as a rejected
    // credential here, sending someone to reset a password that was never the problem.
    if (firebaseError.code === 'auth/internal-error') {
      return { invalidCredentials: false, message: SERVICE_BUSY_ERROR };
    }
    return { invalidCredentials: false, message: SOMETHING_WENT_WRONG_ERROR };
  }
};

export const EMAIL_NOT_VERIFIED_REJECTION_REASON =
  'Signed in but their email was not verified, so they saw the verify-your-email page';

/**
 * Whether there is a signed-in user whose address is unverified. False when nobody is signed in, since there
 * is then no address to be unverified.
 */
export const hasUnverifiedSignedInUser = (): boolean => firebase.auth().currentUser?.emailVerified === false;

export const signInWithCustomToken = async (customToken: string): Promise<void> => {
  await firebase.auth().signInWithCustomToken(customToken);

  // The #neo-events "Sign in" notification for these SSO flows is emitted server-side at the OAuth
  // callback that mints the custom token; the client only clears the auth-attempt markers it set before
  // redirecting.
  localStorage.removeItem(LocalStorageKey.SESSION_GOOGLE_AUTH_ATTEMPT);
  localStorage.removeItem(LocalStorageKey.SESSION_INTUIT_AUTH_ATTEMPT);
};

export const onAuthenticationStateChange = (callback: (user: AuthenticationUser | null) => void): void => {
  // According to firebase documentation...
  //
  // `OnIdTokenChanged`: Adds an observer for changes to the signed-in user's ID
  // token, which includes sign-in, sign-out, and token refresh events. This
  // method has the same behavior as firebase.auth.Auth.onAuthStateChanged had
  // prior to 4.0.0. https://firebase.google.com/docs/reference/js/v8/firebase.auth.Auth#onidtokenchanged
  //
  // `onAuthStateChanged`: Prior to 4.0.0, this triggered the observer when
  // users were signed in, signed out, or when the user's ID token changed in
  // situations such as token expiry or password change. After 4.0.0, the
  // observer is only triggered on sign-in or sign-out.
  // https://firebase.google.com/docs/reference/js/v8/firebase.auth.Auth#onauthstatechanged
  //
  // A stale token is re-minted before the user is published, so nothing reacting to `emailVerified` can
  // send a token the backend rejects. Only the latest event is published: an earlier one still waiting on
  // Firebase must not overwrite a sign-out or account switch that arrived meanwhile. The re-mint fires this
  // observer again; a user whose token still disagrees after one re-mint is published as-is. A re-mint that
  // fails fires nothing, so it is retried on that user's next event.
  let latestEvent = 0;
  let reMintedForUid: string | null = null;
  firebase.auth().onIdTokenChanged(async (user) => {
    const event = ++latestEvent;
    if (user && user.uid !== reMintedForUid && (await hasStaleEmailVerifiedToken(user).catch(() => false))) {
      reMintedForUid = user.uid;
      await user.getIdToken(/* forceRefresh */ true).catch(() => {
        if (reMintedForUid === user.uid) {
          reMintedForUid = null;
        }
      });
    }
    if (event === latestEvent) {
      callback(user);
    }
  });
};

export const isSignInWithEmailLink = (): boolean => firebase.auth().isSignInWithEmailLink(window.location.href);

export const signInWithEmailLink = async (email: string): Promise<void> => {
  await firebase.auth().signInWithEmailLink(email, window.location.href);
};

export const getAuthenticationToken = async (): Promise<string | undefined> =>
  await firebase.auth().currentUser?.getIdToken();

/**
 * Whether `user` reports a verified email that its cached ID token predates — the state of a session
 * restored after verifying elsewhere, whose token the backend's auth guard rejects with EMAIL_NOT_VERIFIED.
 */
export const hasStaleEmailVerifiedToken = async (user: AuthenticationUser): Promise<boolean> => {
  if (!user.emailVerified) {
    return false;
  }
  const { claims } = await user.getIdTokenResult();
  return claims.email_verified !== true;
};

/**
 * Applies a Firebase out-of-band action code to complete an email-verification
 * flow, then force-refreshes the client's ID token so the next API call carries
 * `email_verified: true`.
 *
 * Why the refresh: `applyActionCode` updates Firebase's server-side record and
 * mutates `currentUser.emailVerified` locally, but the cached ID token (which
 * Apollo Client reads via `getIdToken()`) still carries the old claim. Without
 * a forced refresh, every subsequent authenticated request is rejected by the
 * backend's auth guard with EMAIL_NOT_VERIFIED.
 *
 * Rejects with the raw Firebase error so callers can branch on
 * `code === 'auth/expired-action-code'` / `'auth/invalid-action-code'`.
 *
 * Returns the verified email (when the action code exposes one) so the caller can pre-populate
 * the sign-in form on the post-verification redirect.
 */
export const applyEmailVerificationActionCode = async (oobCode: string): Promise<string | undefined> => {
  // Read the verified email off the action code BEFORE applying it — applyActionCode consumes the
  // oobCode, after which it can't be inspected. Returned so the post-verification redirect can
  // pre-populate the sign-in email field. checkActionCode rejects with the same
  // expired/invalid Firebase error codes as applyActionCode, so caller error handling is unchanged.
  const actionCodeInfo = await firebase.auth().checkActionCode(oobCode);
  const verifiedEmail = actionCodeInfo.data.email ?? undefined;
  await firebase.auth().applyActionCode(oobCode);
  const currentUser = firebase.auth().currentUser;
  if (currentUser) {
    // Order matters: refresh the cached ID token BEFORE `reload()` updates
    // local state. `reload()` triggers `onIdTokenChanged`, which pushes a new
    // `emailVerified: true` into the reactive store; anything listening on
    // that flag (useUserStateReconciliation) will fire a reconcile mutation
    // that reads the cached Apollo token. If `getIdToken(true)` hasn't run
    // yet, Apollo sends the stale token and the backend rejects it with
    // EMAIL_NOT_VERIFIED until a retry picks up the refreshed token.
    await currentUser.getIdToken(/* forceRefresh */ true);
    await currentUser.reload();
  }
  return verifiedEmail;
};

export const clearFirebase = (): void => {
  firebase.auth().signOut();
};

/**
 * Force-refreshes the Firebase ID token so the next authenticated API call
 * picks up any server-side user-state changes (e.g. `emailVerified` was flipped
 * since the cached token was minted). No-op when no user is signed in.
 */
export const refreshFirebaseIdToken = async (): Promise<void> => {
  const currentUser = firebase.auth().currentUser;
  if (!currentUser) {
    return;
  }
  await currentUser.reload();
  await currentUser.getIdToken(/* forceRefresh */ true);
};

export const isMaybeFirebaseError = (error: unknown): error is MaybeFirebaseError =>
  typeof error === 'object' && error !== null && 'code' in error && typeof error.code === 'string';

/** The address Identity Platform names on an `auth/account-exists-with-different-credential` error. */
const ConflictingEmailSchema = z.object({ email: z.string().trim().email() });

/**
 * Reads the address Identity Platform names on an account-conflict error.
 *
 * `auth/account-exists-with-different-credential` is the one failure that carries the address it refused,
 * because resolving it requires knowing which account to sign in with. It is also the only place this
 * attempt's address exists in the browser — the popup produced no user to read one from — so it is the only
 * way the audit row can name who was blocked.
 */
const readConflictingEmail = (error: unknown): string | undefined => {
  const parsed = ConflictingEmailSchema.safeParse(error);
  return parsed.success ? parsed.data.email.toLowerCase() : undefined;
};

/** The error codes an ExtendedGraphQLErrors response carries, which is how provisioning reports a refusal. */
const CodedGraphQlErrorSchema = z.object({
  graphQLErrors: z.array(z.object({ extensions: z.object({ errors: z.array(z.object({ code: z.string() })) }) })),
});

/**
 * Reads the reason out of a failure raised by provisioning rather than by Identity Platform.
 *
 * These arrive as a coded GraphQL error from `provisionCurrentUser`, which refuses to attach a sign-in to an
 * account that is not its own. A provider the organization does not use needs the right provider or an
 * administrator; any other identity mismatch needs the method the account was created with.
 *
 * Undefined for anything else, so a genuine fault reports as unknown rather than as an SSO problem.
 */
export const readProvisioningErrorReason = (error: unknown): AuthErrorType | undefined => {
  const parsed = CodedGraphQlErrorSchema.safeParse(error);
  if (!parsed.success) {
    return undefined;
  }
  const codes = parsed.data.graphQLErrors.flatMap(({ extensions }) => extensions.errors.map(({ code }) => code));
  if (codes.includes(ProvisioningErrorCodes.SSO_PROVIDER_MISMATCH)) {
    return AuthErrorType.SSO_PROVIDER_MISMATCH;
  }
  return codes.includes(ProvisioningErrorCodes.ACCOUNT_LINKED_TO_ANOTHER_IDENTITY)
    ? AuthErrorType.ACCOUNT_EXISTS_DIFFERENT_CREDENTIAL
    : undefined;
};

/**
 * Which of the failures Identity Platform has collapsed into `auth/internal-error`.
 *
 * The markers are embedded by the sign-in blocking function's own rejections. Anything else is Identity
 * Platform failing on its own account, in practice the function not answering inside its deadline. Only that
 * and an unreadable account are worth retrying: the attempt that timed out is also the one that warmed the
 * instance, and an account read that failed once normally succeeds the next time.
 */
const readInternalErrorReason = (message: string | undefined): AuthErrorType => {
  // The same reason, and so the same copy, as Firebase's own refusal of this situation. Which of the two
  // rejects the sign-in turns on whether the provider asserted a verified email, which the person reading the
  // message cannot act on.
  if (message?.includes(BlockingFunctionRejectionCodes.ENTERPRISE_LINK_REFUSED)) {
    return AuthErrorType.ACCOUNT_EXISTS_DIFFERENT_CREDENTIAL;
  }
  // The provider authenticated the user but supplied no email address. Retrying cannot change that, so
  // offering a retry would be a loop that cannot succeed.
  if (message?.includes(BlockingFunctionRejectionCodes.NO_PROVIDER_EMAIL)) {
    return AuthErrorType.NO_PROVIDER_EMAIL;
  }
  if (message?.includes(BlockingFunctionRejectionCodes.ACCOUNT_UNREADABLE)) {
    return AuthErrorType.ACCOUNT_CHECK_FAILED;
  }
  return AuthErrorType.SERVICE_BUSY;
};

/**
 * Reads which failure an `auth/internal-error` was for a magic-link sign-in, returning
 * {@link AuthErrorType.SERVICE_BUSY_LOGIN_LINK} where {@link readInternalErrorReason} returns
 * {@link AuthErrorType.SERVICE_BUSY}.
 *
 * A magic link meets the same blocking-function refusals as any other method, and those are unchanged. Only
 * the timeout differs: its copy tells the user to open the link again, which a failed attempt leaves usable.
 */
export const readEmailLinkInternalErrorReason = (message: string | undefined): AuthErrorType => {
  const reason = readInternalErrorReason(message);
  return reason === AuthErrorType.SERVICE_BUSY ? AuthErrorType.SERVICE_BUSY_LOGIN_LINK : reason;
};

export type OAuthLifecycleAdapter = {
  onStart?(): void;
  onSuccess?(): void;
  /**
   * @param reason - why the sign-in failed, when it is something the user can be told. Absent for a
   * failure with nothing to say, such as the user closing the popup themselves.
   *
   * This is how a failed sign-in is reported: {@link startOAuth} settles rather than rejecting, so a
   * caller cannot leave a user staring at a form that stopped for no visible reason simply by not
   * wrapping the call. Anything worth recording has already gone to Sentry by the time this runs.
   */
  onError?(reason?: AuthErrorType): void;
};

/**
 * What {@link startOAuth} needs from the React layer, so this util stays free of Apollo and GraphQL.
 *
 * Every callback is required. Each one is the sole mechanism behind something a caller cannot see is missing —
 * a security check, the DB user, or the only record of a failed sign-in — so an optional field would let a new
 * call site skip one silently.
 */
export type StartOAuthOptions = {
  ssoProvider: SsoProvider;
  adapter: OAuthLifecycleAdapter;
  /** Absent for Google and Microsoft; the Firebase provider id (`oidc.*` / `saml.*`) for an enterprise IdP. */
  enterpriseSsoProviderId: string | undefined;
  /**
   * Resolves the instance a single-tenant domain belongs to, via the useDomainRedirect Apollo hook. SSO cannot
   * be intercepted before the popup, so this check runs on the post-popup address.
   */
  getSingleTenantRedirectUrl: (email: string) => Promise<string | null>;
  /** Creates the DB user through the token-verified provisionCurrentUser mutation. */
  provisionCurrentUser: () => Promise<unknown>;
  /** Audits a sign-in Identity Platform refused, which reaches no backend and so leaves no other trace. */
  reportLoginFailure: (email: string, method: ClientReportableLoginMethod, rejectionReason: string) => void;
};

export const startOAuth = async ({
  adapter,
  enterpriseSsoProviderId,
  getSingleTenantRedirectUrl,
  provisionCurrentUser,
  reportLoginFailure,
  ssoProvider,
}: StartOAuthOptions): Promise<void> => {
  try {
    adapter.onStart?.();

    // Inside the block: building the provider can throw for a misconfigured call, and a throw escaping
    // here would break the promise this function makes to report every failure through the adapter.
    const oauthProvider = getAuthProvider(ssoProvider, enterpriseSsoProviderId);

    const { user } = await firebase.auth().signInWithPopup(oauthProvider);
    const email = await resolveSignedInEmail(user);

    // If the user's email domain belongs to a single-tenant instance, don't provision them on the
    // main app — sign back out and send them to their instance's login.
    const singleTenantRedirectUrl = await getSingleTenantRedirectUrl(email);
    if (singleTenantRedirectUrl) {
      await firebase.auth().signOut();
      // Protocol-guard the dynamic redirect target before assigning to location.href (XSS sink); the
      // early return still prevents main-app provisioning even if the target is rejected.
      const instanceLoginUrl = toHttpUrl('/login', singleTenantRedirectUrl);
      if (!instanceLoginUrl) {
        // Signed out with nowhere to send them. Saying so is all that is left — returning quietly would
        // leave the form spinning on an attempt that already ended.
        captureException(new Error('Single-tenant redirect target was rejected'), {
          extra: { source: 'startOAuth', ssoProvider, singleTenantRedirectUrl },
        });
        adapter.onError?.(AuthErrorType.UNKNOWN);
        return;
      }
      window.location.href = instanceLoginUrl.toString();
      return;
    }

    // Provision the DB user through the token-verified provisionCurrentUser mutation. The Apollo auth
    // link attaches the freshly-popped Firebase session's ID token, from which the server derives the
    // email, uid, and SSO provider (and enterprise provider id) — never client-supplied input.
    await provisionCurrentUser();

    // Propagate the server-side `emailVerified` flip into BOTH the ID token and
    // the cached User object. `provisionCurrentUser` set Firebase's record
    // to verified above, but the token and User object minted
    // by signInWithPopup are frozen at email_verified=false for IdPs that don't
    // upstream the claim (Microsoft, SAML, some OIDC).
    //
    // getIdToken(forceRefresh) re-mints the JWT so authenticated API calls clear
    // the backend's email-verified guard. reload() refreshes the User object's
    // `emailVerified` property — which getIdToken does NOT touch — so the
    // client-side useAuthRedirect guard stops bouncing the user to
    // /verify-email-sent?reason=security-update. Without the reload() the backend
    // accepts the request but the frontend still strands the user on a stale
    // property. Token first, then reload() (matching applyEmailVerificationActionCode)
    // so the reload()-triggered onIdTokenChanged reconciliation reads a fresh
    // cached token.
    await firebase.auth().currentUser?.getIdToken(/* forceRefresh */ true);
    await firebase.auth().currentUser?.reload();

    adapter.onSuccess?.();
  } catch (error) {
    if (!isMaybeFirebaseError(error)) {
      // Provisioning refused to attach this sign-in to the account holding the address. Already reported and
      // audited server-side, with the provider and the address it claimed, so reporting it here would file a
      // second issue for one refusal.
      const provisioningReason = readProvisioningErrorReason(error);
      if (provisioningReason) {
        // Signed out first, as on the single-tenant bounce above: the popup left a live Firebase session that
        // provisioning declined to give a user. Left in place, `useAuthRedirect` reads it as
        // authenticated-but-unprovisioned and sends them to finish signup, which calls provisionCurrentUser
        // again, is refused again, and offers a retry that cannot succeed.
        await firebase.auth().signOut();
        adapter.onError?.(provisioningReason);
        return;
      }

      // Anything else is not something Firebase raised either — most often our own failure to resolve an
      // address from the completed sign-in. Naming it as unknown still beats saying nothing, which strands
      // the user on a form that stopped for no visible reason; reporting it is what makes the cause findable.
      captureException(error, { extra: { source: 'startOAuth', ssoProvider, enterpriseSsoProviderId } });
      adapter.onError?.(AuthErrorType.UNKNOWN);
      return;
    }
    if (error.code === 'auth/internal-error') {
      // Raised whenever sign-in fails inside Identity Platform rather than in the browser, which collapses
      // situations needing different advice. Identity Platform relays the backend's own message
      // inside this error, so the markers are what separate them.
      const reason = readInternalErrorReason(error.message);

      // A refused enterprise link or an unreadable account is already reported from the blocking function, with
      // the evidence for it. Reporting it here too would file a second issue for one refusal and split the
      // evidence between them.
      if (
        reason !== AuthErrorType.ACCOUNT_EXISTS_DIFFERENT_CREDENTIAL &&
        reason !== AuthErrorType.ACCOUNT_CHECK_FAILED
      ) {
        captureException(error, { extra: { source: 'startOAuth', ssoProvider, enterpriseSsoProviderId } });
      }

      adapter.onError?.(reason);
      return;
    }
    if (error.code === 'auth/account-exists-with-different-credential') {
      // Identity Platform refuses to attach this provider to an address that already has an account, and
      // decides that itself: no request reaches our backend, so neither the auth guard nor the sign-in
      // blocking function observes the attempt. Reporting both ways here is what makes a blocked enterprise
      // sign-in visible to us at all.
      //
      // For every provider, not only the ones that dead-end: Google and Microsoft recover by re-signing-in
      // and linking, but the attempt still failed, and omitting the recoverable ones would misreport how
      // often this is hit.
      captureException(error, { extra: { source: 'startOAuth', ssoProvider, enterpriseSsoProviderId } });
      const conflictingEmail = readConflictingEmail(error);
      if (conflictingEmail) {
        reportLoginFailure(conflictingEmail, SSO_PROVIDER_AUDIT_METHODS[ssoProvider], ACCOUNT_EXISTS_REJECTION_REASON);
      }

      // resolve SSO users who signed up previously with the old method (custom token)
      // 1. sign in with the old method (custom token)
      // 2. link the user with the new SSO method (real SSO provider)
      switch (ssoProvider) {
        case SsoProvider.GOOGLE:
          redirectToGoogleOAuth();
          break;
        case SsoProvider.MICROSOFT:
          redirectToMicrosoftOAuth();
          break;
        case SsoProvider.OIDC:
        case SsoProvider.SAML:
          // No redirect exists for an enterprise provider, so this is where the user is told.
          adapter.onError?.(AuthErrorType.ACCOUNT_EXISTS_DIFFERENT_CREDENTIAL);
          break;
        default:
          adapter.onError?.(AuthErrorType.ACCOUNT_EXISTS_DIFFERENT_CREDENTIAL);
          assertNever(ssoProvider);
      }
      return;
    }
    // Closing the popup is the user changing their mind, not a fault — reporting it would fill Sentry
    // with people who thought better of signing in, and no message belongs on screen either.
    if (POPUP_DISMISSAL_CODES.has(error.code ?? '')) {
      adapter.onError?.();
      return;
    }
    captureException(error, { extra: { source: 'startOAuth', ssoProvider, enterpriseSsoProviderId } });
    adapter.onError?.(AuthErrorType.UNKNOWN);
  }
};

export const migrateSsoUser = async (user: firebase.User, ssoProvider: SsoProvider): Promise<void> => {
  const oauthProvider = getAuthProvider(ssoProvider);
  await user.linkWithPopup(oauthProvider);
};

/**
 * Reads the address the sign-in blocking function attached to this session.
 *
 * For a provider that publishes the address under a non-standard name, this is the only place it
 * appears client-side: Firebase cannot map such a claim onto the user record, and a blocking function
 * is not permitted to write `email` there either — so `user.email` is empty even though the provider
 * did supply an address and the backend already read it.
 */
const readSessionClaimEmail = async (user: firebase.User): Promise<string | undefined> => {
  try {
    const { claims } = await user.getIdTokenResult();
    const parsed = z.object({ [EMAIL_SESSION_CLAIM]: z.string().email() }).safeParse(claims);
    return parsed.success ? parsed.data[EMAIL_SESSION_CLAIM] : undefined;
  } catch {
    // A token that cannot be read leaves nothing to fall back on, so let the caller fail on the missing
    // address rather than on a token error the user can do nothing about.
    return undefined;
  }
};

/**
 * Resolves the signed-in user's address, which is all the browser needs it for: routing a single-tenant
 * domain to its own instance before anything is provisioned.
 *
 * Interpreting an identity provider's attributes is the sign-in blocking function's job, not this one's:
 * it sees the provider's claims server-side, knows every attribute name we have encountered, and reports
 * the ones it could not use. Whatever it resolves arrives here on the session, so there are only two
 * places to look — what Firebase itself mapped, and what the blocking function attached — and no reason
 * for the browser to hold a second, narrower copy of that knowledge.
 *
 * The account's name and uid are not read here at all: provisioning derives them from the verified token
 * server-side, so a copy taken from the browser would be both redundant and less trustworthy.
 */
const resolveSignedInEmail = async (user: firebase.User | null): Promise<string> => {
  if (!user) {
    throw new Error('Missing user after Firebase SSO');
  }

  const email = user.email ?? (await readSessionClaimEmail(user));
  if (!email) {
    throw new Error('Missing email after Firebase SSO');
  }

  return email;
};
